/**
 * PunchListCategoryDialog
 * ──────────────────────────────────────────────────────────────────────
 * Lightbox that opens when the user clicks an urgent / attention / done
 * counter in the scheduler header. Shows the filtered punch list with
 * full detail (patient, time, issue, financial impact, Echo handling,
 * primary action) plus a header summary.
 *
 * Pure UI — caller supplies `items` (already filtered by priority) and
 * `onAction(item)` for the action button.
 */
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { cn } from '../../../../lib/utils';
import {
  AlertTriangle, DollarSign, Phone, ScanLine, Sparkles, CheckCircle2,
  Clock,
} from 'lucide-react';

const CATEGORY = {
  red: {
    label: 'Urgent',
    subtitle: 'Items needing immediate attention to avoid revenue loss.',
    icon: AlertTriangle,
    iconClass: 'text-red-500',
    accent: 'bg-red-50 text-red-700 border-red-200',
    dot: 'bg-red-500',
  },
  amber: {
    label: 'Attention',
    subtitle: 'Actions recommended to keep today on track.',
    icon: Clock,
    iconClass: 'text-amber-500',
    accent: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500',
  },
  green: {
    label: 'Done',
    subtitle: "Completed items — you're all caught up here.",
    icon: CheckCircle2,
    iconClass: 'text-emerald-500',
    accent: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500',
  },
  blue: {
    label: 'AI Handling',
    subtitle: 'Items Echo is managing in the background.',
    icon: Sparkles,
    iconClass: 'text-blue-500',
    accent: 'bg-blue-50 text-blue-700 border-blue-200',
    dot: 'bg-blue-500',
  },
};

const ACTION_ICON = {
  call: Phone,
  scan: ScanLine,
  autofill: Sparkles,
  collect: DollarSign,
  book: Sparkles,
};

const PunchListCategoryDialog = ({ open, onOpenChange, category, items = [], onAction }) => {
  const cfg = CATEGORY[category] || CATEGORY.amber;
  const Icon = cfg.icon;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg p-0 gap-0 overflow-hidden"
        data-testid={`punch-category-dialog-${category}`}
        data-dialog-type="punch-category-dialog"
      >
        <DialogHeader className="px-5 py-4 border-b border-slate-100">
          <DialogTitle className="text-base font-semibold flex items-center gap-2">
            <Icon className={cn('h-4 w-4', cfg.iconClass)} />
            {cfg.label}
            <Badge className={cn('text-[10px] ml-1', cfg.accent)}>
              {items.length} {items.length === 1 ? 'item' : 'items'}
            </Badge>
          </DialogTitle>
          <p className="text-[11px] text-slate-500 mt-0.5">{cfg.subtitle}</p>
          <DialogDescription className="sr-only">{cfg.subtitle}</DialogDescription>
        </DialogHeader>

        <div className="max-h-[480px] overflow-y-auto p-4 space-y-2">
          {items.length === 0 && (
            <div
              className="py-8 text-center"
              data-testid={`punch-category-dialog-${category}-empty`}
            >
              <CheckCircle2 className="h-6 w-6 text-emerald-300 mx-auto mb-1.5" />
              <p className="text-[11px] text-slate-500">Nothing in this bucket right now.</p>
            </div>
          )}
          {items.map((item) => {
            const ActionIcon = ACTION_ICON[item.actionType];
            return (
              <div
                key={item.id}
                className={cn('rounded-lg p-3 border', cfg.accent)}
                data-testid={`punch-category-item-${item.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={cn('mt-1 h-2.5 w-2.5 rounded-full shrink-0', cfg.dot)} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900">
                        {item.patient || item.time || '—'}
                      </span>
                      {item.time && item.patient && (
                        <span className="text-[10px] text-slate-500">({item.time})</span>
                      )}
                    </div>
                    <p className="text-[12px] text-slate-700 mt-1">{item.issue}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {item.financial && (
                        <span className="text-[10px] inline-flex items-center gap-1 text-slate-600">
                          <DollarSign className="h-3 w-3" />
                          {item.financial}
                        </span>
                      )}
                      {item.echoHandling && (
                        <span className="text-[10px] text-blue-600 font-medium">
                          {item.echoHandling}
                        </span>
                      )}
                    </div>
                  </div>
                  {item.action && onAction && (
                    <Button
                      size="sm"
                      className={cn(
                        'h-7 text-[10px] gap-1 shrink-0',
                        item.actionType === 'autofill'
                          ? 'bg-[#6b5ce7] hover:bg-[#5a4bd6] text-white'
                          : 'bg-[#111] hover:bg-[#222] text-white'
                      )}
                      onClick={() => {
                        onAction(item);
                        onOpenChange?.(false);
                      }}
                      data-testid={`punch-category-action-${item.id}`}
                    >
                      {ActionIcon && <ActionIcon className="h-2.5 w-2.5" />}
                      {item.action}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PunchListCategoryDialog;
